/* eslint-disable max-lines */
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, ContextStore, ExperimentStore, PageStore, RouteStore } from 'bernie-core';
import { ClientLogger } from 'bernie-client';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { CollectionBusinessModel, INCOMPATIBLE_RATEPLAN } from 'stores/multi-item-store-util';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';
import { UIPageStore } from 'stores/ui-page-store';

import { ButtonType } from 'constants/button-types.ts';
import { DynamicRateRuleViewModel } from 'models/common/common-types';
import { PaymentPlanViewModel, RoomViewModel, ValueAddPromotionViewModel } from 'models/hotel/hotel-offer-types';

import { classNames } from 'uitk-react-utils';

import { allRoomsSchema, upsellSchema } from 'utils/uis-prime-micro-messages';
import { getInputType } from 'utils/lodging-form-utils';

import {
  adaptToCartPropertySearchContextInput,
  adaptToPropertyNaturalKeyInputForPackages,
  adaptToPropertyNaturalKeyInputs,
} from 'components/multi-item/cart-adapter';
import { PropertyNaturalKeyInput, RatePlanFragment } from '__generated__/api';
import { ReserveButton } from './reserve-button';

type LodgingFormInlineFragment = RatePlanFragment.LodgingFormInlineFragment;

interface RoomReserveFormProps {
  analytics?: AnalyticsStore;
  buttonType?: ButtonType;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  dataAttributes?: any;
  dynamicRatePlan?: DynamicRateRuleViewModel;
  experiment?: ExperimentStore;
  formAction: string;
  formMethod?: string;
  fullWidth?: boolean;
  isPinnedRatePlan?: boolean;
  label?: string;
  multiItem?: MultiItemStore;
  option?: number;
  page?: PageStore;
  paymentPlan?: PaymentPlanViewModel;
  ratePlanCode?: string;
  rfrr?: string;
  room: RoomViewModel;
  routeStore?: RouteStore;
  theme?: string;
  uiPage?: UIPageStore;
  valueAddPromotion?: ValueAddPromotionViewModel;
  lodgingForm?: LodgingFormInlineFragment;
}

interface RoomReserveFormState {
  buttonDisabled: boolean;
}

@inject('analytics', 'context', 'currentHotel', 'experiment', 'multiItem', 'page', 'routeStore', 'uiPage')
@observer
export class RoomReserveForm extends React.Component<RoomReserveFormProps, RoomReserveFormState> {
  public static defaultProps: Partial<RoomReserveFormProps> = {
    dataAttributes: {},
    formAction: '/Details',
    formMethod: 'POST',
  };

  private notMRP: boolean;
  private formRef;
  private logger;

  constructor(props) {
    super(props);
    const { paymentPlan } = this.props;
    const { action } = paymentPlan;

    this.state = {
      buttonDisabled: false,
    };

    this.notMRP = action && action !== 'MRP';
    this.formRef = React.createRef();
    this.onSubmit = this.onSubmit.bind(this);
    this.beforeSubmit = this.beforeSubmit.bind(this);
    this.handleMultiItemFailure = this.handleMultiItemFailure.bind(this);
    this.handleSelectProductsFailure = this.handleSelectProductsFailure.bind(this);
    this.logger = ClientLogger.getLoggerWithIdentifier('pwa.component.reserveForm');
    this.ratePlanAnalytics = this.ratePlanAnalytics.bind(this);
    this.selectProducts = this.selectProducts.bind(this);
  }

  public shouldComponentUpdate() {
    return false;
  }

  private onSubmit(event) {
    const formRef = event && event.target;
    if (formRef) {
      event.preventDefault();

      this.props.uiPage.showProgressIndicator(0);
      this.setState({ buttonDisabled: true });
      /* eslint-disable no-undef */
      requestAnimationFrame(() => {
        /* eslint-disable no-undef */
        requestAnimationFrame(() => {
          formRef.submit();
        });
      });
    }
  }

  public ratePlanAnalytics() {
    const { analytics, isPinnedRatePlan, valueAddPromotion, currentHotel, lodgingForm, ratePlanCode, room, paymentPlan } = this.props;
    const offerBookButton = paymentPlan.offerBookButton;
    const form = lodgingForm || offerBookButton;
    const rateUpsell = form?.inputs?.filter(input => input?.name === 'rateUpsell')?.[0]?.value === 'true';
    const roomUpsell = form?.inputs?.filter(input => input?.name === 'roomUpsell')?.[0]?.value === 'true';
    const additionalAmount = form?.inputs?.filter(input => input?.name === 'additionalAmount')?.[0]?.value;
    const propertyUpsellAmount = form?.inputs?.filter(input => input?.name === 'propertyUpsellAmount')?.[0]?.value;

    if (analytics && typeof analytics.trackEvent === 'function' && isPinnedRatePlan) {
      analytics.trackEvent(`.RoomsAndRates.PinnedBookButton`, `User has clicked on reserve button from pinned rate plan`, null);
    }
    if (analytics && typeof analytics.trackEvent === 'function' && valueAddPromotion) {
      analytics.trackEvent(
        `.RoomsAndRates.VAPBookButton`,
        `User has clicked on reserve button from rate plan with ValueAddPromotion`,
        null,
        null,
        true,
        allRoomsSchema(valueAddPromotion.category, valueAddPromotion.offerType, Number(valueAddPromotion.promotionId), currentHotel.hotelId)
      );
    }
    if (analytics && typeof analytics.trackEvent === 'function' && additionalAmount !== undefined && propertyUpsellAmount !== undefined) {
      analytics.trackEvent(
        `.RoomsAndRates.Upsell`,
        `User has clicked on reserve button from rate plan with upsell info`,
        null,
        null,
        true,
        upsellSchema(
          rateUpsell,
          parseInt(additionalAmount, 10),
          ratePlanCode,
          room.roomTypeCode,
          currentHotel.hotelId,
          roomUpsell,
          parseInt(propertyUpsellAmount, 10)
        )
      );
    }
  }

  private selectProducts(e: React.SyntheticEvent) {
    const { multiItem, context, room, currentHotel, ratePlanCode, paymentPlan } = this.props;
    const inventoryType = paymentPlan && paymentPlan.inventoryType;
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    const propertyNaturalKeys: [PropertyNaturalKeyInput] = [
      adaptToPropertyNaturalKeyInputForPackages(
        paymentPlan.checkInDateInput,
        paymentPlan.checkOutDateInput,
        currentHotel?.searchCriteria?.rooms,
        inventoryType,
        room?.roomTypeCode,
        ratePlanCode,
        paymentPlan.ratePlanType,
        currentHotel?.hotelId.toString()
      ),
    ];

    return multiItem
      .selectProducts(context, multiItem.multiItemContext?.id, paymentPlan.multiItemPriceToken, propertyNaturalKeys, null, null)
      .then(redirectUrl => {
        if (window?.location?.href) {
          window.location.href = redirectUrl;
        } else {
          return Promise.reject('window.location.href does not exist');
        }
      })
      .catch(this.handleSelectProductsFailure);
  }

  private beforeSubmit(e: React.SyntheticEvent) {
    const { multiItem, context, room, currentHotel, ratePlanCode, paymentPlan, analytics } = this.props;
    const inventoryType = paymentPlan && paymentPlan.inventoryType;
    const hotelCollect = paymentPlan && paymentPlan.hotelCollect;
    const businessModel = hotelCollect ? CollectionBusinessModel.HC : CollectionBusinessModel.EC;
    const isAttach = paymentPlan && paymentPlan.airAttachmentEnabled;
    const roomIndex = multiItem.showMixMatchExperience ? multiItem.itemCount : 0;
    const queryParamRoomIndex = currentHotel?.searchCriteria && parseInt(currentHotel.searchCriteria.roomIndex, 10);
    const existingItems = multiItem.getExistingItems(queryParamRoomIndex);
    const hasRoomAtIndex = existingItems && existingItems.length > 0;
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('MultiProp.Reserve', 'Clicking on Reserve into multi property experiment', null);
    }

    const addPropertyToCart = newRoomIndex =>
      multiItem
        .addPropertyToCart(
          context,
          adaptToPropertyNaturalKeyInputs(
            paymentPlan.checkInDate,
            paymentPlan.checkOutDate,
            currentHotel?.searchCriteria?.rooms,
            inventoryType,
            room.roomTypeCode,
            ratePlanCode,
            paymentPlan.ratePlanType,
            currentHotel?.hotelId.toString(),
            isAttach,
            newRoomIndex,
            multiItem.showMixMatchExperience,
            false
          ),
          adaptToCartPropertySearchContextInput(
            currentHotel.searchCriteria,
            currentHotel.hotel && currentHotel.hotel.overview && currentHotel.hotel.overview.regionId
          ),
          businessModel,
          analytics,
          paymentPlan && paymentPlan.noCreditCardRequired,
          newRoomIndex
        )
        .catch(this.handleMultiItemFailure);

    // if user is selecting a room from a different property, clear the cart before adding

    if (
      multiItem &&
      multiItem.showMixMatchExperience &&
      multiItem.items &&
      multiItem.items[0] &&
      multiItem.items[0].propertyId &&
      multiItem.items[0].propertyId !== currentHotel.hotelId
    ) {
      return multiItem.emptyCart(context).then(() => {
        if (analytics) {
          analytics.trackEvent('MixMatch.ChangeProperty', 'MixMatch Property Changed', null, null, true);
        }
        addPropertyToCart(0);
      });
    }

    // if we already have a room in the room index we're searching, replace that room
    if (hasRoomAtIndex) {
      const removeId = existingItems[0].id;

      return multiItem.removeItemFromCart(context, removeId).then(() => {
        if (analytics) {
          analytics.trackEvent('MixMatch.ReplaceProperty', 'MixMatch Property Replaced', null, null, true);
        }

        addPropertyToCart(queryParamRoomIndex);
      });
    }

    return addPropertyToCart(roomIndex);
  }

  private handleSelectProductsFailure(reason) {
    const { multiItem } = this.props;
    if (!multiItem.errorMessage) {
      multiItem.setGenericError();
    }
    if (typeof this.logger.logEvent === 'function') {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FAILED_TO_ADD_HOTEL_TO_MULTIITEM_SESSION'), reason);
    }
  }

  private handleMultiItemFailure(reason) {
    const { context, currentHotel, experiment, multiItem, page, routeStore } = this.props;
    if (multiItem && multiItem.itemCount === 0) {
      if (this.formRef && this.formRef.current && typeof this.formRef.current.submit === 'function') {
        this.formRef.current.submit();
      }
      if (typeof this.logger.logEvent === 'function') {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'FALLBACK_TO_STANDALONE_BOOKING'));
      }
    }
    // this is treating rare usecase, where user tries to reserve a rateplan that they shouldn't have seen in 1st place
    else if (reason === INCOMPATIBLE_RATEPLAN) {
      currentHotel.fetch(null, context, multiItem, experiment, page).then(() => {
        if (routeStore && routeStore.queryParams && routeStore.queryParams.pwaDialogNested) {
          routeStore.goBack();
        }
      });
      if (typeof this.logger.logEvent === 'function') {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'INCOMPATIBLE_RATEPLAN'));
      }
    }
  }

  public render() {
    const {
      context,
      dataAttributes,
      fullWidth,
      label,
      option,
      paymentPlan,
      rfrr,
      room,
      currentHotel,
      multiItem,
      buttonType,
      lodgingForm,
    } = this.props;
    const { buttonDisabled } = this.state;
    const finalRfrr = this.notMRP ? rfrr : `MRP${rfrr}`;
    const formClasses = classNames('reserve-form uitk-type-right all-y-padding-one l-y-padding-zero', {
      'uitk-cell ': fullWidth,
    });

    const { offerBookButton } = paymentPlan;

    if (!room || !paymentPlan) {
      return null;
    }

    const form = offerBookButton || lodgingForm;

    if (!form?.inputs || form.inputs.length < 1) {
      return null;
    }

    const buttonProps = {
      description: room.name,
      disabled: buttonDisabled,
      label,
      fullWidth,
      option,
      rfrr: finalRfrr,
      room,
      dataAttributes,
      currentHotel,
      context,
      buttonType,
    };

    if (multiItem?.isPackageShoppingPath && multiItem.isPackageShoppingPath()) {
      return (
        <ReserveButton
          {...buttonProps}
          // 29690_LS_PWA_PLAYBACK_AREA cartInfo will only have value if user is bucketed
          onUserClick={this.selectProducts}
        />
      );
    }

    return (
      <form action={form.action} className={formClasses} method={form.method} onSubmit={this.onSubmit} ref={this.formRef}>
        {form.inputs.map((input, index) => (
          <input type={getInputType(input.type)} key={`reserve-form-book-buttons${index}`} name={input.name} value={input.value} />
        ))}
        <ReserveButton
          {...buttonProps}
          // 29690_LS_PWA_PLAYBACK_AREA cartInfo will only have value if user is bucketed
          onUserClick={
            multiItem && (multiItem.showMultiPropExperience || multiItem.showMixMatchExperience)
              ? this.beforeSubmit
              : this.ratePlanAnalytics
          }
        />
      </form>
    );
  }
}
